/*
 * Copyright (C) 2026 Fluxer Contributors
 *
 * This file is part of Fluxer.
 *
 * Fluxer is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as published by
 * the Free Software Foundation, either version 3 of the License, or
 * (at your option) any later version.
 *
 * Fluxer is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
 * GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General Public License
 * along with Fluxer. If not, see <https://www.gnu.org/licenses/>.
 */

import * as ModalActionCreators from '@app/actions/ModalActionCreators';
import {modal} from '@app/actions/ModalActionCreators';
import {ConfirmModal} from '@app/components/modals/ConfirmModal';
import type {IARContext} from '@app/components/modals/IARModal';
import {IARModal} from '@app/components/modals/IARModal';
import type {GuildRecord} from '@app/records/GuildRecord';
import type {MessageRecord} from '@app/records/MessageRecord';
import type {UserRecord} from '@app/records/UserRecord';
import {canSubmitReport, showReportRestrictionDialog} from '@app/utils/ReportVerificationGate';
import type {I18n} from '@lingui/core';
import {msg} from '@lingui/core/macro';

function openReportModal(context: IARContext): void {
	ModalActionCreators.push(modal(() => <IARModal context={context} />));
}

function openFollowupReportModal(context: IARContext): void {
	globalThis.setTimeout(() => {
		openReportModal(context);
	}, 0);
}

export function openReportMessageModal(message: MessageRecord): void {
	openReportModal({
		type: 'message',
		message,
	});
}

export function openReportUserModal(params: {
	i18n: I18n;
	user: UserRecord;
	guildId?: string;
	message?: MessageRecord;
}): void {
	if (!canSubmitReport()) {
		showReportRestrictionDialog();
		return;
	}

	const {i18n, user, guildId, message} = params;
	const context: IARContext = {
		type: 'user',
		user,
		guildId,
	};

	const description = message
		? i18n._(
				msg`If this report is about a specific message, report that message instead. Message reports give our Safety Team the clearest context, and adding details in the comments can help us review it faster. Only continue with reporting the user as a whole if that message does not capture the broader issue.`,
			)
		: i18n._(
				msg`If this report is about specific messages, report the most relevant message instead. Message reports give our Safety Team the clearest context, and adding details in the comments can help us review it faster. Only continue with reporting the user as a whole if reporting a message would not capture the broader issue.`,
			);

	ModalActionCreators.push(
		modal(() => (
				<ConfirmModal
					title={i18n._(msg`Report User`)}
					description={description}
					message={message}
					primaryText={i18n._(msg`Continue to Report User`)}
					primaryVariant="danger-primary"
					onPrimary={() => openFollowupReportModal(context)}
				/>
		)),
	);
}

export function openReportGuildModal(params: {i18n: I18n; guild: GuildRecord}): void {
	if (!canSubmitReport()) {
		showReportRestrictionDialog();
		return;
	}

	const {i18n, guild} = params;
	const context: IARContext = {
		type: 'guild',
		guild,
	};

	ModalActionCreators.push(
		modal(() => (
				<ConfirmModal
					title={i18n._(msg`Report Community`)}
					description={i18n._(
						msg`If this report is about a specific message in this community, report that message instead. Message reports give our Safety Team the clearest context, and adding details in the comments can help us review it faster. Only continue with reporting the community as a whole if reporting a message would not capture the broader issue.`,
					)}
					primaryText={i18n._(msg`Continue to Report Community`)}
					primaryVariant="danger-primary"
					onPrimary={() => openFollowupReportModal(context)}
				/>
		)),
	);
}
